import { useState } from 'react';
import { ArrowDownCircle, ArrowUpCircle, Check, ImageIcon, X } from 'lucide-react';
import { RechargeStatus, WalletMovementType } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { usePuede } from '@/lib/permisos';
import {
  useAjustarSaldo,
  useRevisarRecarga,
  useSaldoDomiciliario,
  type RecargaAdmin,
} from '@/lib/queries';
import { AreaTexto, Boton, Campo, Cargando, Entrada, EntradaDinero } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const ESTADO: Record<RechargeStatus, { texto: string; clase: string }> = {
  PENDING: { texto: 'En revisión', clase: 'bg-amber-50 text-amber-700' },
  APPROVED: { texto: 'Aprobada', clase: 'bg-emerald-50 text-emerald-700' },
  REJECTED: { texto: 'Rechazada', clase: 'bg-red-50 text-kick-red' },
};

const MOVIMIENTO: Record<string, string> = {
  [WalletMovementType.RECHARGE]: 'Recarga',
  [WalletMovementType.DELIVERY_CUT]: 'Descuento por entrega',
  [WalletMovementType.BONUS]: 'Bono',
  [WalletMovementType.ADJUSTMENT]: 'Ajuste manual',
};

/**
 * El saldo prepago de un domiciliario, visto desde administración.
 *
 * Arriba el saldo y el ajuste manual; luego las recargas —las pendientes
 * primero, con el comprobante a la vista para cotejarlo con el banco— y al
 * final el libro de movimientos, que es lo que responde «¿por qué tiene
 * este saldo?».
 */
export function SaldoDomiciliario({ courierId, nombre }: { courierId: string; nombre: string }) {
  const ficha = useSaldoDomiciliario(courierId);
  const puede = usePuede();
  const puedeMover = puede('domiciliarios', 'saldo');

  if (ficha.isLoading || !ficha.data) return <Cargando texto="Buscando el saldo…" />;

  const { balance, cutPercent, recargas, movimientos } = ficha.data;
  const pendientes = recargas.filter((r) => r.status === RechargeStatus.PENDING);
  const revisadas = recargas.filter((r) => r.status !== RechargeStatus.PENDING);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-kick bg-kick-light p-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-kick-gray">Saldo actual</p>
          <p className={cn('text-3xl font-bold', balance > 0 ? 'text-kick-dark' : 'text-kick-red')}>
            {formatCOP(balance)}
          </p>
          <p className="text-xs text-kick-gray">
            Se descuenta el {cutPercent}% del domicilio por cada entrega.
            {balance <= 0 && ' Sin saldo no puede abrir turno.'}
          </p>
        </div>
        {puedeMover && <Ajuste courierId={courierId} nombre={nombre} />}
      </div>

      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-kick-gray">
          Recargas por revisar {pendientes.length > 0 && `(${pendientes.length})`}
        </h3>
        {pendientes.length === 0 ? (
          <p className="mt-2 text-sm text-kick-gray">Nada pendiente.</p>
        ) : (
          <ul className="mt-2 space-y-3">
            {pendientes.map((r) => (
              <li key={r.id}>
                <RecargaPendiente recarga={r} puedeMover={puedeMover} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-kick-gray">
          Recargas anteriores
        </h3>
        {revisadas.length === 0 ? (
          <p className="mt-2 text-sm text-kick-gray">Todavía no ha recargado.</p>
        ) : (
          <ul className="mt-2 divide-y divide-kick-gray/10 rounded-kick border border-kick-gray/10">
            {revisadas.map((r) => {
              const estado = ESTADO[r.status];
              return (
                <li key={r.id} className="flex items-center gap-3 p-3 text-sm">
                  <a
                    href={r.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-kick-gray hover:text-kick-red"
                    title="Ver comprobante"
                    aria-label="Ver comprobante"
                  >
                    <ImageIcon className="h-4 w-4" />
                  </a>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-kick-ink">
                      {formatCOP(r.approvedAmount ?? r.declaredAmount)}
                      {r.approvedAmount !== null && r.approvedAmount !== r.declaredAmount && (
                        <span className="ml-1 font-normal text-kick-gray">
                          (declaró {formatCOP(r.declaredAmount)})
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-xs text-kick-gray">
                      {new Date(r.createdAt).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
                      {r.reviewNote && ` · ${r.reviewNote}`}
                    </span>
                  </span>
                  <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', estado.clase)}>
                    {estado.texto}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-kick-gray">Movimientos</h3>
        {movimientos.length === 0 ? (
          <p className="mt-2 text-sm text-kick-gray">Sin movimientos todavía.</p>
        ) : (
          <ul className="mt-2 divide-y divide-kick-gray/10 rounded-kick border border-kick-gray/10">
            {movimientos.map((m) => (
              <li key={m.id} className="flex items-center gap-3 p-3 text-sm">
                {m.amount >= 0 ? (
                  <ArrowUpCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <ArrowDownCircle className="h-4 w-4 shrink-0 text-kick-red" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-kick-ink">
                    {MOVIMIENTO[m.type] ?? m.type}
                    {m.orderCode && <span className="font-mono text-kick-gray"> · {m.orderCode}</span>}
                  </span>
                  <span className="block truncate text-xs text-kick-gray">
                    {new Date(m.createdAt).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
                    {m.note && ` · ${m.note}`}
                  </span>
                </span>
                <span className="text-right">
                  <span
                    className={cn(
                      'block font-semibold',
                      m.amount >= 0 ? 'text-emerald-700' : 'text-kick-red',
                    )}
                  >
                    {m.amount >= 0 ? '+' : '−'}
                    {formatCOP(Math.abs(m.amount))}
                  </span>
                  <span className="block text-xs text-kick-gray">queda {formatCOP(m.balanceAfter)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Una recarga esperando decisión.
 *
 * El comprobante se ve grande, porque cotejarlo es todo el trabajo. El valor
 * se puede corregir antes de aprobar: si la foto dice $48.000 y el mensajero
 * escribió $50.000, se aprueba por lo que entró de verdad.
 */
function RecargaPendiente({ recarga, puedeMover }: { recarga: RecargaAdmin; puedeMover: boolean }) {
  const avisos = useAvisos();
  const revisar = useRevisarRecarga();
  const [monto, setMonto] = useState(recarga.declaredAmount);
  const [nota, setNota] = useState('');
  const [rechazando, setRechazando] = useState(false);

  const decidir = (approve: boolean) => {
    revisar.mutate(
      { id: recarga.id, approve, amount: approve ? monto : undefined, note: nota.trim() || undefined },
      {
        onSuccess: () => {
          avisos.exito(approve ? `Recarga aprobada por ${formatCOP(monto)}` : 'Recarga rechazada');
          setRechazando(false);
          setNota('');
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <article className="rounded-kick border border-amber-200 bg-amber-50/40 p-3">
      <div className="flex flex-wrap items-start gap-3">
        <a href={recarga.receiptUrl} target="_blank" rel="noreferrer" className="shrink-0">
          <img
            src={recarga.receiptUrl}
            alt="Comprobante de la recarga"
            className="h-36 w-28 rounded-kick border border-kick-gray/10 bg-white object-contain"
          />
        </a>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm text-kick-ink">
            Declaró <strong>{formatCOP(recarga.declaredAmount)}</strong>
            <span className="block text-xs text-kick-gray">
              {new Date(recarga.createdAt).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
            </span>
          </p>

          {puedeMover && (
            <>
              <Campo etiqueta="Valor a aprobar" ayuda="Corrígelo si el comprobante dice otra cifra">
                <EntradaDinero centavos={monto} onCambio={setMonto} />
              </Campo>

              {rechazando ? (
                <Campo etiqueta="Motivo del rechazo">
                  <AreaTexto
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    rows={2}
                    placeholder="El comprobante no corresponde a nuestra cuenta…"
                  />
                </Campo>
              ) : (
                <Campo etiqueta="Nota para el domiciliario (opcional)">
                  <Entrada
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    placeholder="Ej. Se aprueba por el valor que dice el comprobante"
                  />
                </Campo>
              )}

              <div className="flex flex-wrap justify-end gap-2">
                {rechazando ? (
                  <>
                    <Boton variante="secundario" className="px-3 py-1.5 text-xs" onClick={() => setRechazando(false)}>
                      Volver
                    </Boton>
                    <Boton
                      variante="peligro"
                      className="px-3 py-1.5 text-xs"
                      icono={<X className="h-3.5 w-3.5" />}
                      cargando={revisar.isPending}
                      disabled={nota.trim().length < 3}
                      onClick={() => decidir(false)}
                    >
                      Confirmar rechazo
                    </Boton>
                  </>
                ) : (
                  <>
                    <Boton variante="secundario" className="px-3 py-1.5 text-xs" onClick={() => setRechazando(true)}>
                      Rechazar
                    </Boton>
                    <Boton
                      className="px-3 py-1.5 text-xs"
                      icono={<Check className="h-3.5 w-3.5" />}
                      cargando={revisar.isPending}
                      disabled={monto <= 0}
                      onClick={() => decidir(true)}
                    >
                      Aprobar {formatCOP(monto)}
                    </Boton>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

/** Corrección manual del saldo: con signo y motivo, y queda en el libro. */
function Ajuste({ courierId, nombre }: { courierId: string; nombre: string }) {
  const avisos = useAvisos();
  const ajustar = useAjustarSaldo();
  const [abierto, setAbierto] = useState(false);
  const [monto, setMonto] = useState(0);
  const [resta, setResta] = useState(false);
  const [nota, setNota] = useState('');

  if (!abierto) {
    return (
      <Boton variante="secundario" className="px-3 py-1.5 text-xs" onClick={() => setAbierto(true)}>
        Ajustar saldo
      </Boton>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-kick border border-kick-gray/15 bg-white p-3 sm:w-auto sm:min-w-[20rem]">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setResta(false)}
          className={cn('flex-1 rounded-kick border px-2 py-1.5 text-xs font-semibold', !resta ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-kick-gray/20 text-kick-gray')}
        >
          Sumar
        </button>
        <button
          type="button"
          onClick={() => setResta(true)}
          className={cn('flex-1 rounded-kick border px-2 py-1.5 text-xs font-semibold', resta ? 'border-kick-red bg-red-50 text-kick-red' : 'border-kick-gray/20 text-kick-gray')}
        >
          Restar
        </button>
      </div>
      <Campo etiqueta="Valor">
        <EntradaDinero centavos={monto} onCambio={setMonto} />
      </Campo>
      <Campo etiqueta="Motivo">
        <Entrada value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. Devolución de un descuento cobrado dos veces" />
      </Campo>
      <div className="flex justify-end gap-2">
        <Boton variante="secundario" className="px-3 py-1.5 text-xs" onClick={() => setAbierto(false)}>
          Cancelar
        </Boton>
        <Boton
          className="px-3 py-1.5 text-xs"
          cargando={ajustar.isPending}
          disabled={monto <= 0 || nota.trim().length < 3}
          onClick={() =>
            ajustar.mutate(
              { courierId, amount: resta ? -monto : monto, note: nota.trim() },
              {
                onSuccess: () => {
                  avisos.exito(`Saldo de ${nombre} ajustado`);
                  setAbierto(false);
                  setMonto(0);
                  setNota('');
                },
                onError: (error) => avisos.error(getErrorMessage(error)),
              },
            )
          }
        >
          Aplicar
        </Boton>
      </div>
    </div>
  );
}
